/* eslint-disable react/jsx-handler-names */
import PropTypes from 'prop-types';
import {
  trans,
  getPagePath,
  useSoyuzExperiment
} from '@spotahome/soyuz/client';

import NoSSR from 'react-no-ssr';

import './index.scss';

import { useThemeFlag } from '../../../../base/ThemeProvider';

import { useUserInfoContext } from '../../../../..';

import Button from '../../Button';
import LanguageSwitcher from '../../LanguageSwitcher';

import HeaderLink from '../HeaderLink';
import HeaderItem from '../HeaderItem';
import HeaderLogin from '../HeaderLogin';
import HeaderSupportCenter from '../HeaderSupportCenter';
import HeaderItems from '../HeaderItems';
import Badge from '../../Badge';

import linkShape from '../shapes/link.shape';
import loginShape from '../shapes/login.shape';
import themeShape, { DARK_THEME, LIGHT_THEME } from '../shapes/theme.shape';

const HeaderNav = ({
  login = {},
  color = DARK_THEME,
  isTransparent = false,
  showLogin,
  showMessagesSection = true,
  ...links
}) => {
  const {
    listYourProperty = {},
    tenantLandingPage = null,
    howItWorks = {},
    favorites = {},
    supportCenter = {}
  } = links;

  const newButtonsEnabled = useThemeFlag('newButtons');

  const {
    userInfo: { chat }
  } = useUserInfoContext();

  return (
    <HeaderItems>
      {!!listYourProperty && (
        <HeaderItem>
          <Button
            alt
            color={
              newButtonsEnabled && isTransparent && color === LIGHT_THEME
                ? 'tertiary-light'
                : 'interaction'
            }
            className="ga-home-publish-your-property-top header-button"
            href={getPagePath('externals.landlord-landing')}
            size="small"
            {...listYourProperty}
          >
            {listYourProperty.label || trans('landingpage.button.landlord')}
          </Button>
        </HeaderItem>
      )}
      {!!tenantLandingPage && (
        <HeaderItem>
          <Button
            alt={color === 'dark'}
            color={color === 'light' ? 'filled' : 'primary'}
            className="ga-home-tenant-landing-page-top header-button"
            href={getPagePath('landing')}
            size="small"
            {...tenantLandingPage}
          >
            {tenantLandingPage.label || trans('landingpage.button.tenant')}
          </Button>
        </HeaderItem>
      )}
      {!!howItWorks && (
        <HeaderItem>
          <HeaderLink
            target="_blank"
            className="ga-menu-faqs"
            color={color}
            href={getPagePath('how-it-works')}
            {...howItWorks}
          >
            {howItWorks.label || trans('how_it_works.title')}
          </HeaderLink>
        </HeaderItem>
      )}
      {!!favorites && (
        <HeaderItem>
          <HeaderLink
            className="ga-menu-favorites"
            target="_blank"
            color={color}
            href={getPagePath('marketplace.favorites')}
            rel="nofollow"
            {...favorites}
          >
            {favorites.label || trans('favorites.title.favorites')}
          </HeaderLink>
        </HeaderItem>
      )}

      {showMessagesSection && chat?.openedChannels > 0 && (
        <HeaderItem>
          <HeaderLink
            className="ga-menu-messages"
            target="_blank"
            color={color}
            href={getPagePath('tenant-messages')}
            rel="nofollow"
          >
            {trans('menu.messages.title')}
            {!!chat?.unreadChannels && chat.unreadChannels > 0 && (
              <Badge
                label={String(chat.unreadChannels)}
                theme="primary"
                size="small"
                className="header-nav__badge"
              />
            )}
          </HeaderLink>
        </HeaderItem>
      )}

      {showLogin || !!login ? (
        <NoSSR>
          <HeaderLogin {...login} color={color} isMobile={false} />
        </NoSSR>
      ) : null}

      {!!supportCenter && (
        <HeaderItem>
          <HeaderSupportCenter color={color} supportCenter={supportCenter} />
        </HeaderItem>
      )}
      <HeaderItem>
        <HeaderLink color={color}>
          <LanguageSwitcher theme={color} abbreviated />
        </HeaderLink>
      </HeaderItem>
    </HeaderItems>
  );
};

HeaderNav.propTypes = {
  listYourProperty: PropTypes.shape(linkShape),
  howItWorks: PropTypes.shape(linkShape),
  favorites: PropTypes.shape(linkShape),
  supportCenter: PropTypes.shape(linkShape),
  help: PropTypes.shape(linkShape),
  tenantLandingPage: PropTypes.shape(linkShape),
  login: PropTypes.shape(loginShape),
  color: themeShape,
  isTransparent: PropTypes.bool,
  showLogin: PropTypes.bool.isRequired,
  showMessagesSection: PropTypes.bool
};

export default HeaderNav;
